import { Component, DestroyRef, OnInit, inject } from '@angular/core';

import {
  AboutPanelComponent,
  AboutPanelInputs,
  AuthState,
  LegalDocumentsService,
  NavigationItem,
  PermissionKey,
  PermissionsState,
  SetEnvironment,
  SidePanel,
  SidepanelTypes,
  UserState,
} from '@gea/digital-ui-lib';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { ActivationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '../environments/models/environment.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private store = inject(Store);
  private router = inject(Router);
  private destroyed = inject(DestroyRef);
  private legalDocumentsService = inject(LegalDocumentsService);
  environment = inject<EnvironmentConfiguration>(ENVIRONMENT_CONFIG);

  // <CHANGE ME>
  readonly BLUEPRINT_APP_TITLE = 'Blueprint App';

  tokenReady = false;
  userReady = false;
  noAuth = false;
  userCountry = 'US';
  currentLanguage = '';
  loggedOutPageReady = false;
  headerTitle = 'Blueprint App';

  // <CHANGE ME> - Add the navigation items for the Blueprint App
  navItems: NavigationItem[] = [
    {
      label: 'UI-LIB.GENERAL.HOME',
      route: '/home',
      icon: 'apps',
    },
  ];
  adminNavItems: NavigationItem[] = [];
  botItems: SidePanel[] = [];
  // <CHANGE ME> - Add the routes that do not require authentication
  readonly NO_AUTH_ROUTES: string[] = ['about-gea'];

  private permissions: PermissionKey[] = [];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.store.select(UserState.token).subscribe(() => {
      this.tokenReady = true;
    });
    this.store
      .select(UserState.loggedOutPageReady)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((ready) => {
        this.loggedOutPageReady = !!ready;
      });
    this.store
      .select(UserState.authState)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((authState) => {
        if (authState === AuthState.LogoutRequested) {
          this.tokenReady = false;
          this.loggedOutPageReady = false;
        }
      });
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((event) => event instanceof ActivationEnd)
      )
      .subscribe((event) => {
        this.noAuth = this.NO_AUTH_ROUTES.includes(event.snapshot?.routeConfig?.path || '');
        if (event instanceof ActivationEnd) {
          this.headerTitle = this.getHeaderTitle();
        }
      });
  }

  ngOnInit(): void {
    this.checkUserPermission();

    // This is the official syntax of ngxs
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.store
      .select(UserState.user)
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((user) => !!user.email)
      )
      .subscribe((user) => {
        if (user?.country) {
          this.userCountry = user.country;
        }
        const selectedLanguage = localStorage.getItem('language');
        if (user?.language) {
          this.translate.use(user.language);
        } else if (selectedLanguage) {
          this.translate.use(selectedLanguage);
        } else {
          this.translate.use(this.translate.defaultLang);
        }
        this.userReady = true;
      });

    if (this.environment.name) {
      this.store.dispatch(new SetEnvironment(this.environment.name));
    }
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyed)).subscribe((event: LangChangeEvent) => {
      this.currentLanguage = event.lang;
      this.botItems = [
        {
          key: 'contact',
          icon: 'chat',
          label: 'UI-LIB.NAV-BAR.CONTACT',
          type: SidepanelTypes.CONTACT,
          supportAppBaseUrl: this.environment.support.redirectURL,
        },
        {
          key: 'about',
          icon: 'c-info',
          label: 'UI-LIB.NAV-BAR.ABOUT',
          type: SidepanelTypes.FOOTER,
          ref: AboutPanelComponent,
          inputs: {
            copyright: 'GEA Group Services GmbH 2023',
            footers: [
              {
                key: 'imprint-header',
                header: 'UI-LIB.FOOTER.IMPRINT',
                content: [
                  {
                    key: 'imprint',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.IMPRINT',
                    action: () => this.legalDocumentsService.openImprint(),
                  },
                ],
              },
              {
                key: 'privacy-header',
                header: 'UI-LIB.FOOTER.DATAPRIVACY',
                content: [
                  {
                    key: 'privacy',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.DATAPRIVACY',
                    action: () => this.legalDocumentsService.openDataPrivacy(),
                  },
                ],
              },
              {
                key: 'terms-header',
                header: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                content: [
                  {
                    key: 'terms',
                    icon: 'pdf',
                    label: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
                    action: () => this.legalDocumentsService.openTermsAndConditions(),
                  },
                ],
              },
              {
                key: 'cookie-settings',
                header: 'UI-LIB.FOOTER.COOKIE-SETTINGS',
                content: [
                  {
                    key: 'cookies',
                    label: 'UI-LIB.FOOTER.COOKIES',
                    action: () => this.legalDocumentsService.openCookieSettings(),
                  },
                ],
              },
            ],
          } as AboutPanelInputs,
        },
      ] as SidePanel[];
    });
    this.translate.setDefaultLang('de-DE');
    this.addCookielawScriptTag();
  }

  addCookielawScriptTag() {
    const cookielawId = this.environment.cookielawId;
    if (cookielawId) {
      const script = document.createElement('script');
      script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.setAttribute('data-domain-script', cookielawId);
      const head = document.head;
      const firstScriptTag = head.querySelector('script');
      if (firstScriptTag) {
        head.insertBefore(script, firstScriptTag);
      } else {
        head.appendChild(script);
      }
    }
  }

  checkUserPermission() {
    this.store.select(PermissionsState.userPermissions).subscribe((permissions: PermissionKey[]) => {
      this.permissions = permissions;
    });
  }

  getHeaderTitle() {
    return this.BLUEPRINT_APP_TITLE;
  }
}
